/**
 * The JSP safety meeting (MB's R119; TJR's "JSP Safety Minutes", TJR inventory 01 §6.2; docs/specs/jsp-safety-meeting.md):
 * the crew record the risks found on the recce flight of the survey area and their mitigations, with a picture of the
 * survey map and its hazards, make the PDF and send it for the crew's sign-off through DocuSeal (R120,
 * `./docuseal.ts`). The meeting is a shared record of the project (`jsp_meeting`), recorded offline on any laptop.
 */
import { docusealSignatureTags, SIGNOFF_SIGNERS_MAX, type SignoffState } from './docuseal.js';

export const JSP_MANIFEST_SCHEMA = 'teslyn.jsp-meeting.v1';

/** The limits the screen, the laptop's database and Teslyn Cloud all check. */
export const JSP_LIMITS = {
  location: 200,
  recce: 1000,
  notes: 4000,
  risk: 500,
  mitigation: 1000,
  risks: 100,
  attendees: SIGNOFF_SIGNERS_MAX,
} as const;

export interface JspAttendee {
  readonly person_id: string;
  readonly name: string;
  /** The person's role on the project, as the screen showed it ("Pilot", "Operator"); may be empty. */
  readonly role: string;
}

export interface JspRisk {
  /** Stable within the meeting (a UUID made by the screen). */
  readonly id: string;
  readonly risk: string;
  readonly mitigation: string;
  /** The hazard pin the row was taken from, if any. */
  readonly pin_id: string | null;
}

/** A `jsp_meeting` row as Teslyn Field reads it. */
export interface JspMeeting {
  readonly id: string;
  readonly project_id: string;
  readonly meeting_date: string;
  readonly location: string;
  readonly recce: string;
  readonly attendees: readonly JspAttendee[];
  readonly risks: readonly JspRisk[];
  readonly notes: string;
  readonly map_picture_at: string | null;
  readonly pdf_name: string;
  readonly pdf_sha256: string;
  readonly pdf_revision: number;
  readonly pdf_made_at: string | null;
  readonly recorded_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
}

/** Who may record, change and remove a meeting (TJR inventory §10.1 `safety.minutes.write`, widened to those on site). */
export const JSP_RECORD_ROLES = ['tenant_admin', 'manager', 'field_crew', 'data_processor'] as const;

/** Whether one of `roles` records meetings. A laptop not linked to an organisation yet (no roles) records too. */
export function canRecordJspMeeting(roles: readonly string[] | null): boolean {
  if (roles === null) return true;
  return roles.some((r) => (JSP_RECORD_ROLES as readonly string[]).includes(r));
}

// ------------------------------------------------------------------------------------------------ checks

const DATE = /^\d{4}-\d{2}-\d{2}$/;

function validDate(s: string): boolean {
  if (!DATE.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

export interface JspMeetingDraft {
  readonly meeting_date: string;
  readonly location: string;
  readonly recce: string;
  readonly attendees: readonly JspAttendee[];
  readonly risks: readonly JspRisk[];
  readonly notes: string;
}

/**
 * What is wrong with a meeting before it is saved, in words (empty when it can be saved). A meeting needs its date,
 * at least one attendee and at least one risk; every risk row needs its risk and its mitigation (the TJR minutes
 * listed both); no one is listed twice.
 */
export function checkJspMeeting(m: JspMeetingDraft): string[] {
  const out: string[] = [];
  if (!validDate(m.meeting_date)) out.push('Choose the date of the meeting.');
  if (m.location.trim().length > JSP_LIMITS.location)
    out.push(`Keep the place to ${JSP_LIMITS.location} characters.`);
  if (m.recce.trim().length > JSP_LIMITS.recce)
    out.push(`Keep the recce flight to ${JSP_LIMITS.recce} characters.`);
  if (m.notes.trim().length > JSP_LIMITS.notes)
    out.push(`Keep the other safety topics to ${JSP_LIMITS.notes} characters.`);
  if (m.attendees.length === 0) out.push('Tick who was at the meeting.');
  if (m.attendees.length > JSP_LIMITS.attendees)
    out.push(`A meeting has at most ${JSP_LIMITS.attendees} attendees.`);
  const seen = new Set<string>();
  for (const a of m.attendees) {
    if (seen.has(a.person_id)) out.push(`${a.name} is listed twice.`);
    seen.add(a.person_id);
  }
  if (m.risks.length === 0) out.push('Add the risks found and their mitigations.');
  if (m.risks.length > JSP_LIMITS.risks) out.push(`A meeting has at most ${JSP_LIMITS.risks} risks.`);
  m.risks.forEach((r, i) => {
    const n = i + 1;
    if (!r.risk.trim()) out.push(`Risk ${n}: describe the risk.`);
    else if (r.risk.trim().length > JSP_LIMITS.risk)
      out.push(`Risk ${n}: keep the risk to ${JSP_LIMITS.risk} characters.`);
    if (!r.mitigation.trim()) out.push(`Risk ${n}: write how it is mitigated.`);
    else if (r.mitigation.trim().length > JSP_LIMITS.mitigation)
      out.push(`Risk ${n}: keep the mitigation to ${JSP_LIMITS.mitigation} characters.`);
  });
  return out;
}

/** The pins of a project as this module needs them. */
export interface JspPin {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly deleted_at: string | null;
}

/**
 * One risk row per live hazard pin that is not in the table yet ("Add hazard pins as risks"): the pin's name, with
 * its description after a colon, is the risk; the mitigation is left for the crew to write.
 */
export function jspRiskRowsFromPins(
  pins: readonly JspPin[],
  risks: readonly JspRisk[],
  newId: () => string,
): JspRisk[] {
  const have = new Set(risks.map((r) => r.pin_id).filter((x): x is string => !!x));
  return pins
    .filter((p) => p.deleted_at === null && !have.has(p.id))
    .map((p) => {
      const d = p.description.trim();
      const text = d ? `${p.name.trim()}: ${d}` : p.name.trim();
      return { id: newId(), risk: text.slice(0, JSP_LIMITS.risk), mitigation: '', pin_id: p.id };
    });
}

/** Whether the meeting changed after its PDF was made (or has none): the PDF must be made again before sending. */
export function jspMeetingNeedsNewPdf(
  m: Pick<JspMeeting, 'pdf_made_at' | 'updated_at' | 'pdf_name'>,
): boolean {
  if (!m.pdf_made_at || !m.pdf_name) return true;
  return Date.parse(m.updated_at) > Date.parse(m.pdf_made_at) + 1000;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** `27-October-2026` (the daily report's file date form). */
export function jspFileDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map((x) => Number.parseInt(x, 10));
  return `${d}-${MONTHS[(m ?? 1) - 1]}-${y}`;
}

/**
 * The PDF's file name in `Documents\Job Specific Documents\`: `<project folder>_JSP_Safety_Meeting_<d-Month-yyyy>.pdf`
 * with the meeting date. Never a revision number (R139, MB 2 Nov 2026): the database keeps `pdf_revision`; a PDF made
 * again for the same meeting takes the earlier file's place (decision 30). `signed` names the copy in `Completed and
 * Signed JSA and JSP\`.
 */
export function jspPdfFileName(folderName: string, meetingDate: string, signed = false): string {
  const base = folderName.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'Project';
  return `${base}_JSP_Safety_Meeting_${jspFileDate(meetingDate)}${signed ? '_Signed' : ''}.pdf`;
}

/** The signed copy's name: the signed PDF's own name with `_Signed` before `.pdf` (`signed_file_name` in `jsp.rs`). */
export function signedPdfFileName(pdfName: string): string {
  return `${pdfName.replace(/\.pdf$/i, '')}_Signed.pdf`;
}

/**
 * Decision 940 (Q838 B, 2 Nov 2026): a signed copy that a newer signed copy would replace is kept, not lost, as
 * `<signed name>_superseded_<d-Month-yyyy>.pdf` with the day it was replaced (`_2`, `_3` when that name is taken
 * too); never a revision number. Unsigned PDFs simply take the earlier file's place (`superseded_file_name` in
 * `jsp.rs`).
 */
export function supersededSignedFileName(signedName: string, isoDate: string, n = 1): string {
  const stem = signedName.replace(/\.pdf$/i, '');
  return `${stem}_superseded_${jspFileDate(isoDate)}${n > 1 ? `_${n}` : ''}.pdf`;
}

/** dd/mm/yyyy of a YYYY-MM-DD date. */
export function jspPrintedDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
}

// ------------------------------------------------------------------------------------------------ the PDF manifest

export interface JspManifestTenant {
  readonly key: string;
  readonly name: string;
  readonly brand: string;
}

/** The manifest `report::jsp` renders (mirrored with serde in `crates/teslyn-core/src/report/jsp.rs`). */
export interface JspManifest {
  readonly schema: typeof JSP_MANIFEST_SCHEMA;
  readonly tenant: JspManifestTenant;
  readonly title: string;
  readonly subtitle: string;
  readonly job_number: string;
  readonly cover: readonly (readonly [string, string])[];
  readonly instructions: readonly string[];
  readonly risks: readonly { readonly n: string; readonly risk: string; readonly mitigation: string }[];
  readonly notes: string;
  readonly sign_off_text: string;
  readonly attendees: readonly {
    readonly name: string;
    readonly role: string;
    readonly signature_tag: string;
    readonly date_tag: string;
  }[];
  readonly footer_text: string;
}

/** TJR's instruction lines of the Safety Meeting Minutes and Risk Management Plan pages (`pntjspdc.scp` RiskPages). */
export const JSP_INSTRUCTIONS = [
  'List all risks identified in safety meeting. Notify the Operations Manager if any additional risks are identified from those documented in the JSA. All crew must present.',
  'Create a Risk Management Plan to eliminate the risks identified in the Safety Meeting.',
] as const;

/**
 * TJR's paragraphs 006 and 007 (`pntqtstgs.dat`) in one, for the one sign-off of the meeting's risks and their
 * mitigations.
 */
export function jspSignOffText(jobNumber: string): string {
  return `The signatories below were present at the Safety Meeting and the Risk Management Meeting and are aware of and understand the risks and mitigations listed above and also those identified in the JSA document for Job: ${jobNumber}.`;
}

/** Build the PDF's manifest from a saved meeting. Nothing is computed by the template. */
export function jspManifest(input: {
  readonly tenant: JspManifestTenant;
  readonly project: {
    readonly number: string;
    readonly title: string;
    readonly client_name: string;
    readonly area: string;
    readonly state: string;
  };
  readonly meeting: Pick<
    JspMeeting,
    'meeting_date' | 'location' | 'recce' | 'attendees' | 'risks' | 'notes' | 'recorded_by_name'
  >;
  /**
   * The revision this PDF will be (the meeting's `pdf_revision` + 1): recorded with the PDF in the database, never
   * printed on the document the client reads (R139, MB 2 Nov 2026; the meeting date names it).
   */
  readonly revision: number;
}): JspManifest {
  const { tenant, project: p, meeting: m } = input;
  const area = [p.area, p.state].filter((x) => x.trim()).join(' ');
  const cover: [string, string][] = [
    ['Project', [p.number, p.title].filter((x) => x.trim()).join(' ')],
    ['Client', p.client_name],
    ['Area', area],
    ['Meeting date', jspPrintedDate(m.meeting_date)],
    ['Place', m.location.trim()],
    ['Recce flight', m.recce.trim()],
    ['Recorded by', m.recorded_by_name.trim()],
  ];
  return {
    schema: JSP_MANIFEST_SCHEMA,
    tenant,
    title: 'Job Safety Plan (JSP)',
    subtitle: 'Safety meeting',
    job_number: p.number,
    cover: cover.filter(([, v]) => v !== ''),
    instructions: [...JSP_INSTRUCTIONS],
    risks: m.risks.map((r, i) => ({
      n: String(i + 1),
      risk: r.risk.trim(),
      mitigation: r.mitigation.trim(),
    })),
    notes: m.notes.trim(),
    sign_off_text: jspSignOffText(p.number),
    attendees: m.attendees.map((a, i) => {
      const tags = docusealSignatureTags(i + 1);
      return { name: a.name, role: a.role, signature_tag: tags.signature, date_tag: tags.date };
    }),
    footer_text: `${tenant.name} JSP safety meeting ${p.number} ${jspPrintedDate(m.meeting_date)}`,
  };
}

// ------------------------------------------------------------------------------------------------ sign-off on the laptop

/** A `signoff_request` row as it reaches the laptop (read only, synced down). */
export interface SignoffRequestRow {
  readonly id: string;
  readonly project_id: string;
  readonly document_kind: string;
  readonly document_id: string;
  readonly document_revision: number;
  readonly title: string;
  readonly state: SignoffState;
  readonly signers: readonly {
    readonly person_id: string;
    readonly name: string;
    readonly status: 'awaiting' | 'sent' | 'opened' | 'completed' | 'declined';
    readonly completed_at: string | null;
    readonly declined_at: string | null;
    readonly decline_reason: string | null;
  }[];
  readonly message: string;
  readonly sent_at: string | null;
  readonly completed_at: string | null;
  readonly signed_available: boolean;
  readonly created_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/** An item of this laptop's sign-off queue (`signoff_outbox`). */
export interface SignoffOutboxItem {
  readonly id: string;
  readonly project_id: string;
  readonly document_kind: string;
  readonly document_id: string;
  readonly document_revision: number;
  readonly title: string;
  readonly file_name: string;
  readonly pdf_sha256: string;
  readonly signers: readonly { readonly person_id: string }[];
  readonly queued_at: string;
  readonly attempts: number;
  readonly last_attempt_at: string | null;
  readonly last_error: string;
  /** `queued` (waiting to be sent), `sent` (Teslyn Cloud took it), `refused` (Teslyn Cloud said no: `last_error`). */
  readonly state: 'queued' | 'sent' | 'refused';
}

/** The newest request of a meeting (any revision), and whether it is of the meeting's current PDF. */
export function jspSignoffOf(
  meeting: Pick<JspMeeting, 'id' | 'pdf_revision'>,
  requests: readonly SignoffRequestRow[],
): { readonly request: SignoffRequestRow | null; readonly current: boolean } {
  const mine = requests
    .filter((r) => r.document_kind === 'jsp_meeting' && r.document_id === meeting.id)
    .sort((a, b) => b.document_revision - a.document_revision || b.created_at.localeCompare(a.created_at));
  const request = mine[0] ?? null;
  return { request, current: !!request && request.document_revision === meeting.pdf_revision };
}

/** Why "Send for sign-off" is not offered yet, in words, or null when it can be sent. */
export function jspSendBlocked(
  m: Pick<JspMeeting, 'pdf_made_at' | 'updated_at' | 'pdf_name' | 'pdf_revision' | 'attendees'>,
  signoff: { readonly request: SignoffRequestRow | null; readonly current: boolean },
  queued: SignoffOutboxItem | null,
): string | null {
  if (jspMeetingNeedsNewPdf(m)) return 'Make the PDF first: the meeting changed since it was made.';
  if (m.attendees.length === 0) return 'Tick who was at the meeting.';
  if (queued && queued.state === 'queued' && queued.document_revision === m.pdf_revision)
    return 'Already queued: it is sent for sign-off when Teslyn Field is online and signed in.';
  const r = signoff.current ? signoff.request : null;
  if (r && (r.state === 'sending' || r.state === 'sent')) return 'Already sent for sign-off.';
  if (r && r.state === 'completed') return 'Signed by everyone.';
  return null;
}
